import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  Spinner,
  Alert,
  Form,
  Button,
  Row,
  Col,
  Modal,
} from "react-bootstrap";
import { BASE_URL_API_TRANXT_UAT_API } from "../Utils/Constant";
import * as XLSX from "xlsx";
import swal from "sweetalert";

const Leads = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showRemarkModal, setShowRemarkModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [remark, setRemark] = useState("");
  const today = new Date().toISOString().split("T")[0];
  const [savingRemark, setSavingRemark] = useState(false);

  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const handleRemarkOpen = (lead) => {
    setSelectedLead(lead);
    setRemark(lead.remark || "");
    setShowRemarkModal(true);
  };

  const getPlaceName = async (lat, lng) => {
    if (!lat || !lng) return "-";
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`
      );
      const data = await res.json();
      return data.display_name || "-";
    } catch {
      return "-";
    }
  };
  const fetchLeads = useCallback(async (fromDate, toDate) => {
    setLoading(true);
    setError("");

    try {
      const response = await fetch(`${BASE_URL_API_TRANXT_UAT_API}/getLeads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          from_date: fromDate,
          to_date: toDate,
        }),
      });

      const data = await response.json();

      if (data.status === "0") {
        setLeads(
          data.data.map((l) => ({
            ...l,
            place: "Loading...",
          }))
        );
      } else {
        setLeads([]);
      }
    } catch {
      setError("Server error");
    } finally {
      setLoading(false);
    }
  }, []);
  const handleRemarkSave = async () => {
    if (!remark.trim() || !selectedLead) return;

    setSavingRemark(true);

    try {
      const response = await fetch(`${BASE_URL_API_TRANXT_UAT_API}/AddRemark`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          queryId: String(selectedLead.id),
          remark: remark,
        }),
      });

      const data = await response.json();

      if (data.status === "0" && data.result?.[0]?.result === "Y") {
        const now = new Date().toISOString();

        setLeads((prev) =>
          prev.map((l) =>
            l.id === selectedLead.id
              ? {
                  ...l,
                  remark,
                  remark_inDate: l.remark_inDate || now,
                  remark_update: now,
                }
              : l
          )
        );

        setShowRemarkModal(false);
        setSelectedLead(null);
        setRemark("");

        swal({
          title: "Success",
          text: data.result[0].message || "Lead Marked Successfully",
          icon: "success",
          button: "OK",
        });
      } else {
        swal({
          title: "Error",
          text: data.msg || "Failed to save remark",
          icon: "error",
          button: "OK",
        });
      }
    } catch (err) {
      alert("Server error while saving remark");
    } finally {
      setSavingRemark(false);
    }
  };
  useEffect(() => {
    const fetchPlaces = async () => {
      for (let lead of leads) {
        if (lead.place !== "Loading...") continue;

        const place = await getPlaceName(lead.latitude, lead.longitude);

        setLeads((prev) =>
          prev.map((l) => (l.id === lead.id ? { ...l, place } : l))
        );
      }
    };

    if (leads.length) fetchPlaces();
  }, [leads]);

  useEffect(() => {
    fetchLeads(fromDate, toDate);
  }, [fetchLeads, fromDate, toDate]);

  const handleSearch = () => {
    fetchLeads(fromDate, toDate);
  };

  const handleExportExcel = () => {
    if (!leads.length) return;
    const excelData = leads.map((lead) => ({
      Name: lead.fullname || "-",
      Mobile: lead.mobile || "-",
      Email: lead.email || "-",
      DOB: lead.dob || "-",
      Gender: lead.gender || "-",
      PAN: lead.pan_no || "-",
      Occupation: lead.occupation || "-",
      "Monthly Income": lead.monthly_income || "-",
      "Ongoing Loan": lead.is_ongoing ? "Yes" : "No",
      "Ongoing EMI": lead.ongoing_emi_amount || "-",
      Property: lead.property || "-",
      Place: lead.place || "-",
      "In Time": lead.in_time ? new Date(lead.in_time).toLocaleString() : "-",
      Purpose: lead.purpose || "-",
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData);

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Leads");

    XLSX.writeFile(workbook, `Leads_${fromDate}_to_${toDate}.xlsx`);
  };

  return (
    <div
      className="mt-2"
      style={{
        height: "90vh",
        overflow: "auto",
        scrollbarWidth: "none",
        fontSize: "0.8rem",
        overflowX: "hidden",
      }}
    >
      <Row className="mb-3 align-items-end">
        <Col md={4}>
          <h2 className="mb-3">Leads</h2>
        </Col>

        <Col md={8} className="d-flex justify-content-end align-items-end">
          <Form.Group className="mx-2">
            <Form.Label className="small">From Date</Form.Label>
            <Form.Control
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </Form.Group>

          <Form.Group className="mx-2">
            <Form.Label className="small">To Date</Form.Label>
            <Form.Control
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </Form.Group>

          <Button className="mx-2" variant="primary" onClick={handleSearch}>
            Search
          </Button>

          <Button
            className="mx-2"
            variant="success"
            onClick={handleExportExcel}
            disabled={!leads.length}
          >
            Export Excel
          </Button>
        </Col>
      </Row>

      {loading && (
        <div className="text-center">
          <Spinner animation="border" />
        </div>
      )}

      {error && <Alert variant="danger">{error}</Alert>}

      {!loading && !error && leads.length === 0 && (
        <Alert variant="info">No leads found for the selected date.</Alert>
      )}

      {!loading && leads.length > 0 && (
        <Table bordered hover responsive style={{ fontSize: "0.8rem" }}>
          <thead>
            <tr>
              <th>Name</th>
              <th>Mobile / Email</th>
              <th>DOB</th>
              <th>Gender</th>
              <th>PAN</th>
              <th>Occupation</th>
              <th>Monthly Income</th>
              <th>Ongoing Loan</th>
              <th>Ongoing EMI</th>
              <th>Property</th>
              <th>Place</th>
              <th>In Time</th>
              <th>Purpose</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {leads.map((lead) => (
              <tr key={lead.id}>
                <td>{lead.fullname || "-"}</td>
                <td>
                  {lead.mobile || "-"} <br /> {lead.email || "-"}
                </td>
                <td>{lead.dob || "-"}</td>
                <td>{lead.gender || "-"}</td>
                <td>{lead.pan_no || "-"}</td>
                <td>{lead.occupation || "-"}</td>
                <td>{lead.monthly_income || "-"}</td>
                <td>{lead.is_ongoing ? "Yes" : "No"}</td>
                <td>{lead.ongoing_emi_amount || "-"}</td>
                <td>{lead.property || "-"}</td>
                <td>{lead.place || "-"}</td>
                <td>{new Date(lead.in_time).toLocaleString()}</td>
                <td>{lead.purpose || "-"}</td>
                <td className="text-center">
                  <Button
                    size="sm"
                    variant="warning"
                    onClick={() => handleRemarkOpen(lead)}
                  >
                    Remark
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      <Modal
        show={showRemarkModal}
        onHide={() => setShowRemarkModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Add Remark</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {selectedLead?.remark && (
            <div className="mt-2 small text-muted">
              <div>
                <strong>Remark Created:</strong>{" "}
                {selectedLead.remark_inDate
                  ? new Date(selectedLead.remark_inDate).toLocaleString()
                  : "-"}
              </div>
              <div>
                <strong>Last Updated:</strong>{" "}
                {selectedLead.remark_update
                  ? new Date(selectedLead.remark_update).toLocaleString()
                  : "-"}
              </div>
            </div>
          )}

          <Form.Group>
            <Form.Label>Remark</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              placeholder="Enter remark here..."
            />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRemarkModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleRemarkSave}
            disabled={savingRemark}
          >
            {savingRemark ? "Saving..." : "Save Remark"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Leads;
